import { useState, useEffect, useRef } from "preact/hooks";
import { applyWidgetTheme } from "./useConfig.js";
import type { WidgetConfig } from "./useConfig.js";

interface PreviewInitMessage {
  type: "canary:preview-init";
  config: WidgetConfig;
  accessToken: string;
  tenantId: string;
}

interface ConfigUpdateMessage {
  type: "canary:config-update";
  config: Partial<WidgetConfig>;
}

interface TokenRefreshMessage {
  type: "canary:token-refresh";
  accessToken: string;
}

export function usePreviewConfig() {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [accessToken, setAccessToken] = useState<string>("");
  const [tenantId, setTenantId] = useState<string>("");
  // Track the parent's source for origin validation
  const parentSourceRef = useRef<MessageEventSource | null>(null);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Validate: only accept messages from the embedding parent
      if (event.source !== window.parent) return;

      if (event.data?.type === "canary:preview-init") {
        parentSourceRef.current = event.source;
        const msg = event.data as PreviewInitMessage;
        setConfig(msg.config);
        setAccessToken(msg.accessToken);
        setTenantId(msg.tenantId);
        document.documentElement.style.setProperty("--primary", msg.config.primaryColor);
        applyWidgetTheme(msg.config.theme || "auto");
      }
      if (event.data?.type === "canary:config-update") {
        const msg = event.data as ConfigUpdateMessage;
        setConfig((prev) => (prev ? { ...prev, ...msg.config } : null));
        if (msg.config.primaryColor) {
          document.documentElement.style.setProperty("--primary", msg.config.primaryColor);
        }
        if (msg.config.theme) {
          applyWidgetTheme(msg.config.theme);
        }
      }
      if (event.data?.type === "canary:token-refresh") {
        const msg = event.data as TokenRefreshMessage;
        setAccessToken(msg.accessToken);
      }
      if (event.data?.type === "canary:chat-approved") {
        // Token may be refreshed alongside chat approval
        if (event.data.accessToken) {
          setAccessToken(event.data.accessToken);
        }
      }
    }
    window.addEventListener("message", handleMessage);
    // Tell parent we're ready
    window.parent.postMessage({ type: "canary:preview-ready" }, "*");
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return { config, accessToken, tenantId, loading: !config };
}
